import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';

/**
 * Keeps a route's component instance (and so, for Community, its embedded
 * forum iframe) alive across navigation instead of Angular's default
 * destroy-and-recreate. Reloading the iframe on every visit reset the
 * embedded forum's own client-side state - scroll position, an in-progress
 * post being drafted - so leaving to check another page and coming back felt
 * like opening Community from scratch every time.
 *
 * Opted in via `data: { keepAlive: true }` on the route (see
 * community-routing.module.ts), not a component-class check: this strategy is
 * provided eagerly in app.module.ts, so importing a lazy-loaded component class
 * here would pull that feature module into the main bundle.
 *
 * Detaching (not destroying) is the mechanism `RouteReuseStrategy` exists for:
 * Angular keeps the component and its DOM subtree - including the live iframe
 * element - in memory rather than tearing it down, so re-attaching it doesn't
 * reload the iframe's `src`. Routes without the flag keep Angular's default
 * behaviour (`shouldReuseRoute` below matches the built-in
 * `DefaultRouteReuseStrategy`).
 */
@Injectable()
export class CommunityRouteReuseStrategy implements RouteReuseStrategy {
  private readonly detached = new Map<string, DetachedRouteHandle>();

  private keepAliveKey(route: ActivatedRouteSnapshot): string | null {
    return route.routeConfig?.data?.['keepAlive'] ? route.routeConfig.path ?? '' : null;
  }

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return this.keepAliveKey(route) !== null;
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.keepAliveKey(route);
    if (key === null) {
      return;
    }
    if (handle) {
      this.detached.set(key, handle);
    } else {
      this.detached.delete(key);
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.keepAliveKey(route);
    return key !== null && this.detached.has(key);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.keepAliveKey(route);
    return key !== null ? this.detached.get(key) ?? null : null;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig;
  }
}
